import {
  Box,
  Button,
  CircularProgress,
  Container,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { Field, Form, Formik } from "formik";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import AuthContainer from "../../../components/AuthContainer";
import Logo from "../../../components/Logo";
import { useAuth } from "../../../context/AuthContext";
import { fetchAndStorePermissions } from "../../../utils/checkPermission";
import {
  emailValidation,
  passwordValidation,
} from "../../../utils/validationSchemas";

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const initialValues = {
    email: "",
    password: "",
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/login`,
        {
          identifier: values.email,
          password: values.password,
        },
      );

      if (response.status === 200) {
        const userData = response.data.user;

        login(userData);
        await fetchAndStorePermissions();
        navigate("/dashboard");
      }
    } catch (error) {
      setError(error.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContainer>
      <Container
        maxWidth="xs"
        sx={{ flex: 1, display: "flex", alignItems: "center" }}
      >
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              mb: 4,
            }}
          >
            <Logo />
          </Box>

          <Typography variant="h4" gutterBottom>
            Login
          </Typography>
          <Typography variant="body2" gutterBottom>
            Please enter your credentials to access the system.
          </Typography>

          {error && (
            <Box sx={{ width: "100%", mb: 2 }} color="error.light">
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <Formik
            initialValues={initialValues}
            validationSchema={Yup.object({
              email: emailValidation,
              password: passwordValidation,
            })}
            onSubmit={handleSubmit}
          >
            {({ errors, touched }) => (
              <Form>
                {/* Email Field */}
                <Field
                  name="email"
                  as={TextField}
                  placeholder="Email"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />

                {/* Password Field */}
                <Field
                  name="password"
                  as={TextField}
                  placeholder="Password"
                  type="password"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                />

                <Typography
                  sx={{
                    textDecoration: "underline",
                    textAlign: "right",
                    cursor: "pointer",
                  }}
                  onClick={() => navigate("/reset-password")}
                >
                  Forgot password?
                </Typography>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ mt: 3, py: 1.5, fontWeight: "bold" }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : "Login"}
                </Button>
              </Form>
            )}
          </Formik>
        </Box>
      </Container>
    </AuthContainer>
  );
};

export default LoginPage;
